import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingBuffer from './LoadingBuffer';
import { getProductStock } from '../utils/cartHelper';

const PRODUCTS_API =
  'https://api.vanyacollectionsrajahmundry.com/products/all';

const CATEGORIES_API =
  'https://api.vanyacollectionsrajahmundry.com/categories/';

const VanyaCollections = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();

  // ============================================================
  // DEFAULT IMAGE
  // ============================================================

  const DEFAULT_CATEGORY_IMAGE =
    'https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&q=80&w=500';

  // ============================================================
  // NORMALIZE CATEGORY
  // Handles:
  // "Designer Sarees"
  // "DesignerSarees"
  // "DESIGNER SAREES"
  // ============================================================

  const normalizeCategory = (value) => {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  };

  // ============================================================
  // FETCH PRODUCTS + CATEGORIES
  // ============================================================

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [productsResponse, categoriesResponse] =
          await Promise.all([
            fetch(PRODUCTS_API),
            fetch(CATEGORIES_API),
          ]);

        if (!productsResponse.ok) {
          throw new Error('Failed to fetch products');
        }

        if (!categoriesResponse.ok) {
          throw new Error('Failed to fetch categories');
        }

        const productsData =
          await productsResponse.json();

        const categoriesData =
          await categoriesResponse.json();

        // ======================================================
        // PRODUCTS
        // ======================================================

        setProducts(
          Array.isArray(productsData)
            ? productsData.filter((p) => !p.is_hold)
            : []
        );

        // ======================================================
        // CATEGORIES
        // ======================================================

        setCategories(
          Array.isArray(categoriesData)
            ? categoriesData
            : []
        );

        console.log(
          'PRODUCTS:',
          productsData
        );

        console.log(
          'CATEGORIES:',
          categoriesData
        );

      } catch (error) {
        console.error(
          'COLLECTION FETCH ERROR:',
          error
        );

        setProducts([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    const handleSync = () => fetchData();
    window.addEventListener('focus', handleSync);
    window.addEventListener('storage', handleSync);
    window.addEventListener('vanya_category_sync', handleSync);
    return () => {
      window.removeEventListener('focus', handleSync);
      window.removeEventListener('storage', handleSync);
      window.removeEventListener('vanya_category_sync', handleSync);
    };
  }, []);

  // ============================================================
  // CATEGORY COUNT
  // ============================================================

  const getCategoryCount = (category) => {
    const normalizedCategory =
      normalizeCategory(category);

    const categoryProducts = products.filter((product) => {
      const productCategory =
        normalizeCategory(product.category);

      return (
        productCategory === normalizedCategory
      );
    });

    return {
      total: categoryProducts.length,
      inStock: categoryProducts.filter((p) => getProductStock(p) > 0).length,
    };
  };

  // ============================================================
  // GET CATEGORY IMAGE
  // ============================================================

  const getCategoryImage = (category) => {
    // category can be the complete category object
    if (
      category &&
      typeof category === 'object' &&
      category.image_url
    ) {
      return category.image_url;
    }

    return DEFAULT_CATEGORY_IMAGE;
  };

  // ============================================================
  // CLICK CATEGORY
  // ============================================================

  const handleCategoryClick = (category) => {
    const categoryName =
      typeof category === 'object'
        ? category.category
        : category;

    navigate(
      `/category/${encodeURIComponent(categoryName)}`
    );
  };

  // ============================================================
  // CAROUSEL SCROLL
  // ============================================================

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const {
      scrollLeft,
      scrollWidth,
      clientWidth,
    } = scrollContainerRef.current;

    const maxScroll =
      scrollWidth - clientWidth;

    if (maxScroll > 0) {
      const percentage =
        scrollLeft / maxScroll;

      const newIndex = Math.min(
        2,
        Math.floor(percentage * 3)
      );

      setActiveIndex(newIndex);
    }
  };

  // ============================================================
  // SCROLL LEFT
  // ============================================================

  const scrollLeft = () => {
    if (!scrollContainerRef.current) return;

    scrollContainerRef.current.scrollBy({
      left: -250,
      behavior: 'smooth',
    });
  };

  // ============================================================
  // SCROLL RIGHT
  // ============================================================

  const scrollRight = () => {
    if (!scrollContainerRef.current) return;

    scrollContainerRef.current.scrollBy({
      left: 250,
      behavior: 'smooth',
    });
  };

  // ============================================================
  // DOT SCROLL
  // ============================================================

  const scrollToDot = (index) => {
    if (!scrollContainerRef.current) return;

    const {
      scrollWidth,
      clientWidth,
    } = scrollContainerRef.current;

    const maxScroll =
      scrollWidth - clientWidth;

    const targetScroll =
      (maxScroll / 2) * index;

    scrollContainerRef.current.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });

    setActiveIndex(index);
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <div style={styles.container}>
        <LoadingBuffer 
          message="Loading Our Exquisite Collections..." 
          tag="✦ BROWSE BY CATEGORY ✦"
          minHeight="360px"
        />
      </div>
    );
  }

  // ============================================================
  // UI
  // ============================================================

  return (
    <div style={styles.container}>

      {/* ======================================================
          HEADER
      ====================================================== */}

      <header style={styles.header}>

        <p style={styles.subHeader}>
          ✦ BROWSE BY CATEGORY ✦
        </p>

        <h1 style={styles.mainTitle}>
          Our Exquisite Collections
        </h1>

        <div style={styles.underline}></div>

        <p style={styles.headerDesc}>
          Immerse yourself in our royal handloom
          masterpieces tailored for elegance.
        </p>

      </header>

      {/* ======================================================
          NO CATEGORIES
      ====================================================== */}

      {categories.length === 0 ? (

        <div style={styles.noCategories}>
          No categories available.
        </div>

      ) : (

        <div style={styles.carouselWrapperOuter}>

          <div style={styles.carouselWrapper}>

            {/* ==================================================
                LEFT BUTTON
            ================================================== */}

            <button
              style={styles.navButtonLeft}
              onClick={scrollLeft}
              aria-label="Scroll Left"
            >
              ‹
            </button>

            {/* ==================================================
                CAROUSEL
            ================================================== */}

            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              style={styles.carouselTrack}
              className="carousel-track"
            >

              {categories.map((category, index) => {

                const categoryName =
                  String(
                    category.category || ''
                  ).trim();

                const { total: totalCount, inStock: inStockCount } =
                  getCategoryCount(
                    categoryName
                  );

                const isCategoryOutOfStock =
                  totalCount > 0 && inStockCount === 0;

                const image =
                  getCategoryImage(category);

                return (

                  <div
                    key={
                      category.id ||
                      `${categoryName}-${index}`
                    }
                    style={styles.card}
                    className="collection-card"
                    onClick={() =>
                      handleCategoryClick(
                        category
                      )
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {

                      if (
                        e.key === 'Enter' ||
                        e.key === ' '
                      ) {
                        handleCategoryClick(
                          category
                        );
                      }

                    }}
                  >

                    {/* ========================================
                        RING
                    ======================================== */}

                    <div
                      style={styles.ringGlow}
                    ></div>

                    {/* ========================================
                        IMAGE
                    ======================================== */}

                    <div
                      style={styles.imageWrapper}
                    >

                      <img
                        src={image}
                        alt={categoryName}
                        style={styles.image}
                        className="card-image"
                        loading="lazy"
                        onError={(e) => {

                          if (
                            e.currentTarget.src !==
                            DEFAULT_CATEGORY_IMAGE
                          ) {
                            e.currentTarget.src =
                              DEFAULT_CATEGORY_IMAGE;
                          }

                        }}
                      />

                    </div>

                    {/* ========================================
                        CONTENT
                    ======================================== */}

                    <div
                      style={styles.cardContent}
                    >

                      <div
                        style={styles.contentBanner}
                      >

                        <h3
                          style={styles.cardTitle}
                        >
                          {categoryName}
                        </h3>

                        {isCategoryOutOfStock ? (
                          <p
                            style={{
                              ...styles.cardSub,
                              color: '#ff6b6b',
                              fontWeight: '700',
                            }}
                          >
                            OUT OF STOCK
                          </p>
                        ) : (
                          <p
                            style={styles.cardSub}
                          >
                            {totalCount}{' '}
                            {totalCount === 1
                              ? 'DESIGN'
                              : 'DESIGNS'}
                          </p>
                        )}

                      </div>

                    </div>

                  </div>

                );
              })}

            </div>

            {/* ==================================================
                RIGHT BUTTON
            ================================================== */}

            <button
              style={styles.navButtonRight}
              onClick={scrollRight}
              aria-label="Scroll Right"
            >
              ›
            </button>

          </div>

          {/* ====================================================
              DOTS
          ==================================================== */}

          <div style={styles.dotsContainer}>

            {[0, 1, 2].map((dotIndex) => (

              <button
                key={dotIndex}
                onClick={() =>
                  scrollToDot(dotIndex)
                }
                style={{
                  ...styles.dot,
                  ...(activeIndex === dotIndex
                    ? styles.activeDot
                    : {}),
                }}
                aria-label={`Go to slide ${
                  dotIndex + 1
                }`}
              />

            ))}

          </div>

        </div>

      )}

      {/* ========================================================
          CSS
      ======================================================== */}

      <style>
        {`
          .collection-card {
            transition:
              transform 0.35s cubic-bezier(0.165, 0.84, 0.44, 1),
              box-shadow 0.35s cubic-bezier(0.165, 0.84, 0.44, 1);
            flex-shrink: 0;
            cursor: pointer;
            will-change: transform;
          }

          .collection-card:hover {
            transform: translateY(-6px) scale(1.02);
            box-shadow:
              0 16px 36px rgba(53, 19, 61, 0.28),
              0 0 25px rgba(226, 188, 83, 0.45) !important;
          }

          .collection-card:hover .card-image {
            transform: scale(1.08);
          }

          .card-image {
            transition:
              transform 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
            will-change: transform;
          }

          .carousel-track::-webkit-scrollbar {
            display: none;
          }

          .carousel-track {
            -ms-overflow-style: none;
            scrollbar-width: none;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
          }

          .collection-card {
            scroll-snap-align: start;
          }

          @media (max-width: 768px) {
            .collection-card {
              width: 145px !important;
              height: 145px !important;
            }

            .carousel-wrapper {
              padding-left: 20px !important;
              padding-right: 20px !important;
            }
          }

          @media (max-width: 380px) {
            .collection-card {
              width: 130px !important;
              height: 130px !important;
            }
          }
        `}
      </style>

    </div>
  );
};

// ============================================================
// STYLES
// ============================================================

const styles = {

  container: {
    fontFamily: '"Playfair Display", serif',
    background:
      'radial-gradient(circle at 50% 0%, #fcf8fd 0%, #f4eaf6 55%, #ebdcf0 100%)',
    padding: '20px 16px 16px',
    width: '100%',
    boxSizing: 'border-box',
    overflowX: 'hidden',
  },

  header: {
    textAlign: 'center',
    padding: '16px 20px 20px',
    boxSizing: 'border-box',
  },

  subHeader: {
    fontFamily: '"Montserrat", sans-serif',
    color: '#bfa136',
    fontSize: '11px',
    letterSpacing: '3px',
    marginBottom: '10px',
    fontWeight: '600',
  },

  mainTitle: {
    fontSize: 'clamp(2rem, 5vw, 3.2rem)',
    color: '#35133d',
    margin: '0',
    fontWeight: '700',
  },

  underline: {
    width: '70px',
    height: '2px',
    background:
      'linear-gradient(90deg, transparent, #e2bc53, transparent)',
    margin: '15px auto',
  },

  headerDesc: {
    fontFamily: '"Montserrat", sans-serif',
    color: '#6c5073',
    fontSize: 'clamp(14px, 2vw, 15px)',
    maxWidth: '500px',
    margin: '0 auto',
    letterSpacing: '0.4px',
    lineHeight: '1.5',
  },

  carouselWrapperOuter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto',
  },

  carouselWrapper: {
    position: 'relative',
    width: '100%',
    padding: '20px 40px 10px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
  },

  carouselTrack: {
    display: 'flex',
    flexDirection: 'row',
    gap: '28px',
    overflowX: 'auto',
    scrollBehavior: 'smooth',
    width: '100%',
    padding: '12px 10px',
  },

  navButtonLeft: {
    position: 'absolute',
    left: '5px',
    zIndex: 10,
    backgroundColor: '#35133d',
    color: '#f5d470',
    border: '1.5px solid #e2bc53',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    fontSize: '24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow:
      '0 4px 10px rgba(53, 19, 61, 0.2)',
  },

  navButtonRight: {
    position: 'absolute',
    right: '5px',
    zIndex: 10,
    backgroundColor: '#35133d',
    color: '#f5d470',
    border: '1.5px solid #e2bc53',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    fontSize: '24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow:
      '0 4px 10px rgba(53, 19, 61, 0.2)',
  },

  dotsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    marginTop: '12px',
    marginBottom: '10px',
  },

  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#d8c4df',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },

  activeDot: {
    width: '24px',
    borderRadius: '4px',
    backgroundColor: '#35133d',
    boxShadow:
      '0 0 8px rgba(226, 188, 83, 0.6)',
  },

  card: {
    position: 'relative',
    borderRadius: '50%',
    overflow: 'hidden',
    width: '185px',
    height: '185px',
    border: '3px solid #e8cc68',
    boxShadow:
      '0 12px 30px rgba(53, 19, 61, 0.2), inset 0 0 10px rgba(226, 188, 83, 0.4)',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  ringGlow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '50%',
    border:
      '1px solid rgba(255, 255, 255, 0.6)',
    pointerEvents: 'none',
    zIndex: 3,
  },

  imageWrapper: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    overflow: 'hidden',
  },

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },

  cardContent: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '52%',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    paddingBottom: '16px',
    background:
      'linear-gradient(to top, rgba(35, 12, 40, 0.95) 30%, rgba(35, 12, 40, 0.7) 80%, transparent 100%)',
    zIndex: 2,
    boxSizing: 'border-box',
  },

  contentBanner: {
    backgroundColor:
      'rgba(35, 12, 40, 0.85)',
    border:
      '1px solid rgba(226, 188, 83, 0.5)',
    borderRadius: '16px',
    padding: '4px 12px',
    textAlign: 'center',
    width: '82%',
    boxSizing: 'border-box',
    boxShadow:
      '0 4px 10px rgba(0,0,0,0.3)',
  },

  cardTitle: {
    margin: 0,
    fontSize: '10px',
    color: '#f5d470',
    fontWeight: '700',
    letterSpacing: '0.8px',
    lineHeight: '1.2',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  cardSub: {
    fontFamily: '"Montserrat", sans-serif',
    margin: '2px 0 0',
    fontSize: '8px',
    color: 'rgba(253, 248, 242, 0.9)',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    fontWeight: '500',
  },

  noCategories: {
    textAlign: 'center',
    padding: '40px 20px',
    fontFamily: '"Montserrat", sans-serif',
    color: '#6c5073',
    fontSize: '14px',
  },
};

export default VanyaCollections;